import React, { Fragment } from 'react';
import { motion } from 'framer-motion';
import { MessageCircle, Search, Mountain, ClipboardCheck, ArrowRight } from 'lucide-react';
import { CalButton } from './ui/CalButton';
import { useLanguage } from '../contexts/LanguageContext';
import { journeyCopy } from '../data/translations/journey';
const stepIcons: React.ReactNode[] = [<MessageCircle size={24} strokeWidth={1.5} data-id="element-94" data-mp="1u1z5atjxkb2" />, <Search size={24} strokeWidth={1.5} data-id="element-95" data-mp="plfqpejvpbuz" />, <Mountain size={24} strokeWidth={1.5} data-id="element-96" data-mp="pmifngxn5489" />, <ClipboardCheck size={24} strokeWidth={1.5} data-id="element-97" data-mp="ukk2mcy6tnp0" />];
export function JourneySection() {
  const {
    language
  } = useLanguage();
  const copy = journeyCopy[language];
  const steps = copy.steps.map((step, index) => ({
    ...step,
    number: String(index + 1),
    icon: stepIcons[index]
  }));
  return <section id="journey" className="surface-a under-hero-curve relative pb-24" data-id="element-98" data-mp="31aqj2ovvtcil">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10" data-id="element-99" data-mp="35cv9zfuy3v1j">
        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="text-center mb-16" data-id="element-100" data-mp="2zfk3znjy4fkb">
          <h2 className="font-serif text-4xl md:text-5xl text-ink-900 mb-5" data-id="element-101" data-mp="38gi805udwn2w">
            {copy.title}
          </h2>
          <div className="flex items-center justify-center gap-2 mb-6" data-id="element-102" data-mp="34dvfe2pw2y4n">
            <span className="h-px w-12 bg-line" data-id="element-103" data-mp="3idwhflhw4blp"></span>
            <span className="w-1.5 h-1.5 rounded-full bg-bronze-400" data-id="element-104" data-mp="3iccvpfdgt8cj"></span>
            <span className="h-px w-12 bg-line" data-id="element-105" data-mp="3detkkoc2dbot"></span>
          </div>
          <p className="text-ink-600 text-base md:text-lg leading-relaxed max-w-3xl mx-auto" data-id="element-106" data-mp="3s4imwmvw7lqo">
            {copy.intro}
          </p>
        </motion.div>

        {/* Flowchart */}
        <div data-id="element-107" data-mp="4z9ufy6ow9dz">
          <div className="flex flex-row flex-wrap items-stretch justify-center gap-y-6 gap-x-2 sm:gap-x-3" data-id="element-108" data-mp="4yzzrxap9e1y">
            {/* Starting Point A */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="flex flex-col items-center justify-center gap-2 flex-shrink-0 w-[110px] self-center" data-id="element-109" data-mp="29nll20n2lt">
              <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-[rgba(255,255,255,0.55)] border-2 border-bronze-400/50 flex items-center justify-center font-serif text-xl sm:text-2xl text-bronze-400" data-id="element-110" data-mp="37jkxz2ujckun">
                A
              </div>
              <span className="text-ink-600 text-xs sm:text-sm font-medium text-center leading-snug" data-id="element-111" data-mp="3hgfs79jrzz34">
                {copy.startLabel}
              </span>
            </motion.div>

            {/* Dotted connector */}
            <div className="flex items-center self-center flex-shrink-0" data-id="element-112" data-mp="3qgl0dlwsn1ic">
              <div className="w-4 sm:w-6 border-t-2 border-dotted border-bronze-400/50" data-id="element-113" data-mp="1s4dx3znvob0i"></div>
            </div>

            {/* Steps with arrows between */}
            {steps.map((step, index) => <Fragment key={step.number} data-id="element-114" data-mp="1ompztpk30mkl" data-mpi={index}>
                <motion.div initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.1 + index * 0.15,
              ease: 'easeOut'
            }} className="relative flex flex-shrink-0 w-[150px] sm:w-[168px] lg:w-[180px]" data-id="element-115" data-mp="1juf0oox614t3">
                  <div className="w-full bg-[rgba(255,255,255,0.55)] border border-cardline rounded-2xl py-5 px-4 flex flex-col items-center text-center" data-id="element-116" data-mp="22lfz9budiyc4">
                    <div className="flex items-center gap-2 mb-3" data-id="element-117" data-mp="1tkrjablf6nwl">
                      <div className="w-8 h-8 rounded-full bg-bronze-500 text-white font-serif font-bold text-sm flex items-center justify-center" data-id="element-118" data-mp="1tmkodmgdx02h">
                        {step.number}
                      </div>
                      <div className="w-10 h-10 rounded-full bg-bronze-400/10 border border-bronze-400/30 flex items-center justify-center text-bronze-400" data-id="element-119" data-mp="261r41l9q78k9">
                        {step.icon}
                      </div>
                    </div>
                    <h3 className="font-serif text-base sm:text-lg text-ink-900 mb-2 leading-snug" data-id="element-120" data-mp="1x2dt0o5yebxf">
                      {step.title}
                    </h3>
                    <div className="w-8 h-px bg-line mb-2" data-id="element-121" data-mp="2osidsnxml0z6"></div>
                    <p className="text-ink-600 text-xs sm:text-sm leading-relaxed" data-id="element-122" data-mp="2oq5w8bvs4smn">
                      {step.description}
                    </p>
                    <p className="text-bronze-600 font-medium text-[11px] sm:text-xs mt-auto pt-2" data-id="element-123" data-mp="2fsdgj6m5biyq">
                      {step.sessions}
                    </p>
                  </div>
                </motion.div>

                {index < steps.length - 1 && <motion.div initial={{
              opacity: 0
            }} whileInView={{
              opacity: 1
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.4,
              delay: 0.25 + index * 0.15
            }} className="flex items-center self-center flex-shrink-0" data-id="element-124" data-mp="14errrmk77jdq">
                    <ArrowRight size={20} className="text-bronze-400/70" strokeWidth={1.5} data-id="element-125" data-mp="1armb1i6x3kz1" />
                  </motion.div>}
              </Fragment>)}

            {/* Arrow before B */}
            <motion.div initial={{
            opacity: 0
          }} whileInView={{
            opacity: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: 0.85
          }} className="flex items-center self-center flex-shrink-0" data-id="element-126" data-mp="d5yb4nb96kir">
              <ArrowRight size={20} className="text-bronze-400" strokeWidth={1.5} data-id="element-127" data-mp="3vi4k5wpe83sz" />
            </motion.div>

            {/* Ending Point B */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.95
          }} className="flex flex-col items-center justify-center gap-2 flex-shrink-0 w-[110px] self-center" data-id="element-128" data-mp="1uflwaalp7wqm">
              <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-bronze-500 flex items-center justify-center font-serif text-xl sm:text-2xl text-white font-bold" data-id="element-129" data-mp="289wbo1bt2ek0">
                B
              </div>
              <span className="text-bronze-600 text-xs sm:text-sm font-medium text-center leading-snug" data-id="element-130" data-mp="1kih67zoqfh4j">
                {copy.endLabel}
              </span>
            </motion.div>
          </div>
        </div>

        {/* Outro + CTA */}
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        ease: 'easeOut'
      }} className="mt-14 text-center" data-id="element-131" data-mp="2vwme33s2gb55">
          <p className="text-ink-600 text-base md:text-lg leading-relaxed max-w-2xl mx-auto mb-7" data-id="element-132" data-mp="3b2icnik93mo6">
            {copy.outro}
          </p>
          <CalButton data-id="element-133" data-mp="31a2uak51kw2r">{copy.cta}</CalButton>
        </motion.div>
      </div>
    </section>;
}